import React, { useState } from 'react';
import './UserDetailsForm.css';

const UserDetailsForm = ({ onSubmit, isLoading = false, onCancel = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telephone: '',
    acceptsPromotionalEmails: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Telephone validation (basic)
    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Telephone number is required';
    } else if (formData.telephone.trim().length < 8) {
      newErrors.telephone = 'Please enter a valid telephone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean the form data
      const cleanedData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        telephone: formData.telephone.trim(),
        acceptsPromotionalEmails: formData.acceptsPromotionalEmails
      };

      await onSubmit(cleanedData);
    } catch (error) {
      console.error('Failed to submit user details:', error);
      setErrors({
        submit: error.message || 'Failed to save your details. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="user-details-form-overlay">
      <div className="user-details-form-container">
        <div className="form-header">
          <h3>👋 Welcome to Boris Sales Assistant!</h3>
          <p>Please share your contact details to get started. This helps us provide you with personalized assistance.</p>
        </div>

        <form onSubmit={handleSubmit} className="user-details-form">
          {/* Name Field */}
          <div className="form-field">
            <label htmlFor="name">
              Full Name <span className="required">*</span>
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className={errors.name ? 'error' : ''}
              disabled={isSubmitting}
              autoComplete="name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          {/* Email Field */}
          <div className="form-field">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              className={errors.email ? 'error' : ''}
              disabled={isSubmitting}
              autoComplete="email"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          {/* Telephone Field */}
          <div className="form-field">
            <label htmlFor="telephone">
              Telephone Number <span className="required">*</span>
            </label>
            <input
              id="telephone"
              type="tel"
              name="telephone"
              value={formData.telephone}
              onChange={handleInputChange}
              placeholder="Enter your telephone number"
              className={errors.telephone ? 'error' : ''}
              disabled={isSubmitting}
              autoComplete="tel"
            />
            {errors.telephone && <span className="error-message">{errors.telephone}</span>}
          </div>

          {/* Promotional Emails Checkbox */}
          <div className="form-field checkbox-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="acceptsPromotionalEmails"
                checked={formData.acceptsPromotionalEmails}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
              <span className="checkbox-text">
                I would like to receive promotional emails about new features and special offers
              </span>
            </label>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="submit-error">
              <span>❌ {errors.submit}</span>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="cancel-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            )}
            
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? (
                <>
                  <span className="spinner"></span>
                  Starting Chat...
                </>
              ) : (
                <>
                  🚀 Start Conversation
                </>
              )}
            </button>
          </div>
        </form>

        {/* Privacy Notice */}
        <div className="privacy-notice">
          <p>
            <small>
              🔒 Your information is secure and will only be used to enhance your chat experience. 
              We respect your privacy and will not share your details with third parties.
            </small>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsForm;